import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, CheckCircle2, FileText, Users, Video } from "lucide-react";
import { EmptyState, StatCard, StatusBadge } from "@/components/bits";
import { KanbanBoard } from "@/components/KanbanBoard";
import { Button } from "@/components/ui/button";
import api, { fmtDate, inr } from "@/lib/api";

export default function ConsultantDesk() {
  const [jobs, setJobs] = useState(null);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [apps, setApps] = useState([]);

  const load = useCallback(() => {
    api.get("/consultant/jobs").then(({ data }) => setJobs(data.jobs)).catch(() => setJobs([]));
    api.get("/consultant/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const loadApps = useCallback(async (job) => {
    setSelected(job);
    try {
      const { data } = await api.get(`/consultant/jobs/${job.id}/applications`);
      setApps(data.applications);
    } catch {
      toast.error("Could not load applications");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!jobs) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div data-testid="consultant-desk">
      {stats && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8" data-testid="consultant-stats">
          <StatCard label="Assigned Jobs" value={stats.jobs} icon={Briefcase} />
          <StatCard label="Candidates" value={stats.candidates} icon={Users} />
          <StatCard label="In Interview" value={stats.in_interview} icon={Video} />
          <StatCard label="Offers" value={stats.offers} icon={FileText} />
          <StatCard label="Joined" value={stats.joined} icon={CheckCircle2} accent />
        </div>
      )}
      {!selected ? (
        <>
          <p className="text-sm text-muted-foreground mb-6">
            Jobs assigned to Job Spring HR Consultants. Select a job to manage its pipeline.
          </p>
          {jobs.length === 0 ? (
            <EmptyState title="No consultant jobs right now" description="Jobs where employers chose Job Spring HR Consultant assistance will appear here." cta="Refresh" onCta={load} testId="consultant-empty" />
          ) : (
            <div className="grid gap-4">
              {jobs.map((j) => (
                <div key={j.id} className="bg-white border border-border/60 rounded-xl p-6 flex flex-wrap items-center justify-between gap-4 card-hover" data-testid={`consultant-job-${j.id}`}>
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="font-heading font-semibold text-[#0B1B3D]">{j.title}</p>
                      <StatusBadge status={j.status} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {j.company_name} · {j.location} · {inr(j.salary_min)}–{inr(j.salary_max)} · published {fmtDate(j.published_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold">{j.application_count} candidates</span>
                    <Button size="sm" className="rounded-full bg-[#0B1B3D]" onClick={() => loadApps(j)} data-testid={`manage-${j.id}`}>
                      Manage Pipeline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="mb-4" data-testid="back-to-jobs">
            ← Back to assigned jobs
          </Button>
          <h2 className="font-heading font-bold text-xl text-[#0B1B3D] mb-6">{selected.title} — {selected.company_name}</h2>
          {apps.length === 0 ? (
            <EmptyState title="No candidates yet" description="Applications and partner submissions will appear here." testId="consultant-apps-empty" />
          ) : (
            <KanbanBoard applications={apps} onRefresh={() => loadApps(selected)} showJob={false} />
          )}
        </>
      )}
    </div>
  );
}
